import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { useEffect } from 'react';
import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import Content from './pages/Content';
import AddContent from './pages/AddContent';
import ContentDetail from './pages/ContentDetail';
import ContentEdit from './pages/ContentEdit';
import ContentSearch from './pages/ContentSearch';
import Campaigns from './pages/Campaigns';
import CampaignDetail from './pages/CampaignDetail';
import CampaignEdit from './pages/CampaignEdit';
import Analytics from './pages/Analytics';
import Documentation from './pages/Documentation';
import { Users } from './pages/Users';
import UserSettings from './pages/UserSettings';
import Login from './pages/Login';
import VersionInfo from './components/VersionInfo';
import { useAuthStore } from './store/authStore';
import { useThemeStore } from './store/themeStore';

// Platform Components
import Platforms from './pages/Platforms';
import PlatformDetail from './pages/PlatformDetail';

// Super Admin Components
import SuperAdminLayout from './components/super-admin/SuperAdminLayout';
import SuperAdminDashboard from './components/super-admin/Dashboard';
import UserList from './components/super-admin/users/UserList';
import UserForm from './components/super-admin/users/UserForm';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const { isAuthenticated, checkAuth } = useAuthStore();
  const { isDarkMode } = useThemeStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Theme initialization
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  if (!isAuthenticated) {
    return (
      <QueryClientProvider client={queryClient}>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
          <Login />
          <VersionInfo expandable />
          <Toaster position="top-right" />
        </div>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          
          {/* Super Admin Routes */}
          <Route path="/super-admin" element={<SuperAdminLayout />}>
            <Route index element={<SuperAdminDashboard />} />
            <Route path="dashboard" element={<SuperAdminDashboard />} />
            <Route path="users" element={<UserList />} />
            <Route path="users/create" element={<UserForm mode="create" />} />
            <Route path="users/:userId/edit" element={<UserForm mode="edit" />} />
            {/* TODO: Add more super admin routes */}
          </Route>
          
          {/* Regular User Routes */}
          <Route path="/" element={<Layout />}>
            <Route index element={<Dashboard />} />
            <Route path="content" element={<Content />} />
            <Route path="content/add" element={<AddContent />} />
            <Route path="content/:id" element={<ContentDetail />} />
            <Route path="content/:id/edit" element={<ContentEdit />} />
            <Route path="content-search" element={<ContentSearch />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="campaigns/:id" element={<CampaignDetail />} />
            <Route path="campaigns/:id/edit" element={<CampaignEdit />} />
            <Route path="platforms" element={<Platforms />} />
            <Route path="platforms/:id" element={<PlatformDetail />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="users" element={<Users />} />
            <Route path="user-settings" element={<UserSettings />} />
            <Route path="documentation" element={<Documentation />} />
          </Route>
        </Routes>
        <VersionInfo expandable />
        <Toaster position="top-right" />
      </Router>
    </QueryClientProvider>
  );
}

export default App;